import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  esPestanaInicial,
  type CatalogosDelInforme,
  type Informe,
  type PESTANAS,
  type PestanaDeMercado,
} from "@/lib/informes";
import { cn } from "@/lib/utils";

import {
  estadoDeLaPestana,
  estadoDeMercado,
  estadoDeTerreno,
  type ClaveDeEstadoDePestana,
  type EstadoDeLaPestana,
} from "./estadoDeLaPestana";

/**
 * Cómo se ve y cómo se dice cada estado.
 *
 * LOS COLORES SON TOKENS Y NO LOS HEX DEL DISEÑO, aunque coincidan casi exacto
 * —`danger-dot`, `success-dot`, `ink` e `input` son literalmente los mismos
 * valores—: un hex suelto no acompaña al tema oscuro, y esta barra es lo
 * primero que se ve al abrir un informe.
 *
 * `rotulo` NO es decoración. El punto de color se lo pierden quien usa un lector
 * de pantalla y quien no distingue rojo de verde, así que el estado viaja
 * además como texto en el nombre accesible de la tarjeta.
 */
const ESTADO: Record<
  ClaveDeEstadoDePestana,
  { punto: string; contador: string; rotulo: string; titulo: string; chip: string }
> = {
  falta: {
    punto: "bg-danger-dot border-danger-dot",
    contador: "text-danger-soft-foreground",
    rotulo: "falta un dato obligatorio",
    titulo: "Falta un dato obligatorio",
    chip: "bg-danger-soft text-danger-soft-foreground",
  },
  curso: {
    punto: "bg-warning-dot border-warning-dot",
    contador: "text-warning-soft-foreground",
    rotulo: "empezada, incompleta",
    titulo: "En progreso",
    chip: "bg-warning-soft text-warning-soft-foreground",
  },
  listo: {
    punto: "bg-success-dot border-success-dot",
    contador: "text-success-soft-foreground",
    rotulo: "sin pendientes",
    titulo: "Sin pendientes",
    chip: "bg-success-soft text-success-soft-foreground",
  },
  // El ÚNICO con borde punteado y relleno vacío: dibuja "acá todavía no hay
  // nada", que es distinto de un dato que falta.
  nuevo: {
    punto: "bg-card border-ink-ghost border-dashed",
    contador: "text-ink-hint",
    rotulo: "todavía no empezada",
    titulo: "No empezada",
    chip: "bg-muted text-ink-hint",
  },
  na: {
    punto: "bg-input border-input",
    contador: "text-ink-hint",
    rotulo: "declarada no aplica",
    titulo: "No aplica",
    chip: "bg-muted text-ink-hint",
  },
  dato: {
    punto: "bg-ink border-ink",
    contador: "text-ink-muted",
    rotulo: "referencias enlazadas, solo lectura",
    titulo: "Solo lectura",
    chip: "bg-muted text-ink-muted",
  },
};

type GrupoDePestana = "expediente" | "tecnica" | "mercado" | "terreno";

const ROTULO_DEL_GRUPO: Record<GrupoDePestana, string> = {
  expediente: "Expediente",
  tecnica: "Tasación técnica",
  mercado: "Mercado",
  terreno: "Terreno",
};

interface PestanaDibujable {
  valor: string;
  etiqueta: string;
  titulo: string;
  descripcion: string;
  grupo: GrupoDePestana;
  estado: EstadoDeLaPestana;
  /** Pisa el rótulo accesible del estado cuando el genérico no describe la pestaña. */
  rotulo?: string;
}

interface BarraDePestanasProps {
  informe: Informe;
  catalogos: CatalogosDelInforme;
  /** Las del expediente que este informe muestra hoy: tres, o las diez. */
  pestanas: typeof PESTANAS;
  /**
   * Las dos de mercado con su conteo, o vacío cuando no corresponden — sea
   * porque el informe todavía no se aceptó o porque el módulo está apagado.
   * `cantidad` en `null` es "el conteo todavía no llegó".
   */
  mercado: { pestana: PestanaDeMercado; etiqueta: string; cantidad: number | null }[];
  /**
   * El registro de terreno con su conteo, o ausente cuando el informe todavía
   * no se aceptó. `cantidad` en `null` es "el conteo todavía no llega".
   */
  terreno?: { etiqueta: string; cantidad: number | null } | null;
}

const DE_MERCADO: Record<PestanaDeMercado, { titulo: string; descripcion: string }> = {
  cbr: {
    titulo: "Transacciones del Conservador enlazadas",
    descripcion: "Transacciones del Conservador enlazadas desde Referencias de mercado. Solo lectura.",
  },
  ofertas: {
    titulo: "Ofertas de mercado enlazadas",
    descripcion: "Ofertas de mercado enlazadas desde Referencias de mercado. Solo lectura.",
  },
};

const DE_TERRENO = {
  titulo: "Registro de terreno",
  descripcion:
    "Todo lo que envió el encargado de terreno desde la App: textos, notas de voz, fotografías y coordenadas. Puedes descartar lo que no corresponda.",
};

/**
 * Las pestañas con su estado, por grupo. La usan la barra y el encabezado, y
 * vive en UN lugar para que la tarjeta y el título nunca digan dos estados
 * distintos de la misma pestaña.
 */
function armarPestanas({ informe, catalogos, pestanas, mercado, terreno }: BarraDePestanasProps) {
  const delExpediente = (p: (typeof PESTANAS)[number], grupo: GrupoDePestana) => ({
    valor: p.seccion,
    etiqueta: p.etiqueta,
    titulo: p.titulo,
    descripcion: p.descripcion,
    grupo,
    estado: estadoDeLaPestana(p.seccion, informe, catalogos),
  });

  const iniciales = pestanas
    .filter(({ seccion }) => esPestanaInicial(seccion))
    .map((p) => delExpediente(p, "expediente"));

  const tecnicas = pestanas
    .filter(({ seccion }) => !esPestanaInicial(seccion))
    .map((p) => delExpediente(p, "tecnica"));

  const deMercado: PestanaDibujable[] = mercado.map(({ pestana, etiqueta, cantidad }) => ({
    valor: pestana,
    etiqueta,
    ...DE_MERCADO[pestana],
    grupo: "mercado",
    estado: estadoDeMercado(cantidad),
  }));

  const deTerreno: PestanaDibujable[] = terreno
    ? [
        {
          valor: "terreno",
          etiqueta: terreno.etiqueta,
          ...DE_TERRENO,
          grupo: "terreno",
          estado: estadoDeTerreno(terreno.cantidad),
          rotulo: "registros de terreno, solo lectura",
        },
      ]
    : [];

  return { iniciales, tecnicas, deMercado, deTerreno };
}

/**
 * La barra de pestañas del editor.
 *
 * DOS BANDAS Y NO UNA FILA CON SCROLL, que es el cambio de fondo. Con doce
 * pestañas en una sola línea la barra scrollea de entrada en los equipos de
 * Consultek (viewport de ~800 px) y nada indica que haya más a la derecha: la
 * mitad del expediente queda invisible. El stakeholder pidió que se vean TODAS
 * a la vez, para saber de un vistazo qué falta.
 *
 * LAS BANDAS NO SON NAVEGACIÓN. Se probaron pestañas verticales y el
 * stakeholder las descartó: una columna de destinos se lee como otro nivel y se
 * confunde con el menú principal, que ya vive a la izquierda. Acá el
 * agrupamiento es solo visual — un rótulo y un salto de línea, sin nada que
 * abrir ni cerrar.
 */
export function BarraDePestanas(props: BarraDePestanasProps) {
  const { iniciales, tecnicas, deMercado, deTerreno } = armarPestanas(props);

  /* EL RÓTULO SE ARMA CON LO QUE DE VERDAD HAY EN LA BANDA, no con "el informe
     está aceptado". No es lo mismo: el módulo de mercado se puede apagar en un
     despliegue, y ahí un informe aceptado tiene sus diez pestañas técnicas y
     ninguna de mercado — un rótulo que dijera "Mercado" estaría nombrando una
     banda vacía. */
  const bandas = [
    {
      grupo: [
        "Expediente",
        ...(deMercado.length ? ["Mercado"] : []),
        ...(deTerreno.length ? ["Terreno"] : []),
      ].join(" · "),
      pestanas: [...iniciales, ...deMercado, ...deTerreno],
    },
    ...(tecnicas.length ? [{ grupo: "Tasación técnica", pestanas: tecnicas }] : []),
  ];

  return (
    <TabsList>
      {bandas.map((banda) => (
        <div key={banda.grupo} className="flex min-w-0 flex-col gap-1">
          <span className="text-ink-hint pl-[0.125rem] text-[0.59375rem] font-extrabold tracking-[0.08em] uppercase">
            {banda.grupo}
          </span>
          {/* LAS TARJETAS SON BLOQUES QUE BAJAN DE FILA, no columnas que se
              aprietan. Eran siete columnas FIJAS y a poco de angostar la ventana
              la tarjeta quedaba más angosta que su propia palabra más larga: el
              rótulo desbordaba su caja y se montaba encima del contador
              ("Antecede0/13tes"). Un ancho mínimo por bloque lo hace imposible —
              antes de bajar de ahí, la grilla arma una fila menos.

              `auto-fill` Y NO `auto-fit`, que es lo que mantiene las dos bandas
              alineadas: `auto-fit` colapsa las columnas vacías, así que la banda
              de cinco tarjetas las estiraría hasta ocupar el ancho entero y
              dejaría de coincidir con la de siete que tiene debajo. */}
          <div className="grid min-w-0 grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-[0.375rem]">
            {banda.pestanas.map((pestana) => (
              <TarjetaDePestana key={pestana.valor} pestana={pestana} />
            ))}
          </div>
        </div>
      ))}
    </TabsList>
  );
}

/**
 * El título de la pestaña activa, bajo la barra.
 *
 * EXISTE PORQUE EL BORDE VERDE NO ALCANZABA. Con doce tarjetas parecidas, la
 * activa se distinguía por un contorno, y al bajar a trabajar en el formulario
 * no quedaba nada en pantalla que dijera dónde se estaba. Acá va el nombre
 * COMPLETO —"Descriptor de aguas", no "Aguas"—, de qué trata, y el mismo estado
 * que muestra su tarjeta.
 *
 * LA POSICIÓN CUENTA EN EL ORDEN DEL EXPEDIENTE, no en el de la barra: la barra
 * sube las de mercado a la primera banda para que entren las doce, pero en el
 * informe vienen al final. Y cuenta solo las que el informe muestra hoy: antes
 * de aceptar son tres, y "de 12" nombraría nueve que no existen en pantalla.
 */
export function EncabezadoDePestana({
  activa,
  ...props
}: BarraDePestanasProps & { activa: string }) {
  const { iniciales, tecnicas, deMercado, deTerreno } = armarPestanas(props);
  const enOrden = [...iniciales, ...tecnicas, ...deMercado, ...deTerreno];
  const indice = enOrden.findIndex((p) => p.valor === activa);

  if (indice === -1) return null;

  const pestana = enOrden[indice];
  const estado = ESTADO[pestana.estado.estado];
  const dosCifras = (n: number) => String(n).padStart(2, "0");

  return (
    <div className="border-border mt-4 flex flex-wrap items-end gap-4 border-b-[1.5px] pb-3">
      <div className="min-w-0 flex-1">
        <p className="text-ink-hint flex items-center gap-2 text-[0.75rem] font-extrabold tracking-[0.07em] uppercase">
          <span>{ROTULO_DEL_GRUPO[pestana.grupo]}</span>
          <span aria-hidden="true" className="text-ink-ghost">
            /
          </span>
          <span className="font-mono tracking-normal">
            Pestaña {dosCifras(indice + 1)} de {dosCifras(enOrden.length)}
          </span>
        </p>
        <h2 className="text-h2 text-ink mt-1">{pestana.titulo}</h2>
        <p className="text-ink-muted mt-1 max-w-[45rem] text-sm text-pretty">
          {pestana.descripcion}
        </p>
      </div>

      <div className="flex flex-none items-center gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-bold",
            estado.chip,
          )}
        >
          <span
            aria-hidden="true"
            className={cn("size-[0.5rem] flex-none rounded-full border-[1.5px]", estado.punto)}
          />
          {estado.titulo}
        </span>
        {pestana.estado.chip ? (
          <span className="bg-muted text-ink-body rounded-md px-2.5 py-1.5 font-mono text-sm font-bold">
            {pestana.estado.chip}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function TarjetaDePestana({ pestana }: { pestana: PestanaDibujable }) {
  const { punto, contador, rotulo: rotuloDelEstado } = ESTADO[pestana.estado.estado];
  const rotulo = pestana.rotulo ?? rotuloDelEstado;
  const chip = pestana.estado.chip;

  return (
    <TabsTrigger
      value={pestana.valor}
      /* El nombre completo y el estado en palabras. El `title` no lo anuncia
         todo lector de pantalla —por eso además va el `sr-only` de abajo— pero
         con el mouse es lo único que explica de qué pestaña se trata cuando el
         rótulo va acortado ("Aguas" por "Descriptor de aguas"). */
      title={`${pestana.titulo} — ${rotulo}${chip ? ` · ${chip}` : ""}`}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mt-[0.25rem] size-[0.5rem] flex-none rounded-full border-[1.5px]",
          punto,
        )}
      />
      {/* `min-h` de dos líneas para que una tarjeta de rótulo corto ("Aguas") y
          una de rótulo largo ("Maquinarias y equipos") midan lo mismo y la
          banda no quede dentada.

          `break-words` ES EL CERROJO, no una prolijidad. El ancho mínimo de la
          grilla ya hace que no haga falta, pero un rótulo más largo que los de
          hoy —o una traducción— volvería a desbordar la caja y a montarse sobre
          el contador. Con esto, lo peor que pasa es que una palabra se parta:
          feo, y legible. El defecto anterior no se veía como un error sino como
          un dato ("Antecede0/13tes"), que es lo caro. */}
      <span className="min-h-[1.6875rem] min-w-0 flex-1 leading-[1.25] tracking-[-0.01em] break-words">
        {pestana.etiqueta}
      </span>
      {chip ? (
        <span className={cn("mt-[0.0625rem] flex-none font-mono text-[0.59375rem] font-bold", contador)}>
          {chip}
        </span>
      ) : null}
      {/* El estado, para quien no ve el punto de color. Va al final para que el
          nombre accesible empiece por el rótulo de la pestaña, que es como se
          la busca. */}
      <span className="sr-only">— {rotulo}</span>
    </TabsTrigger>
  );
}
